import React, { useState, useCallback, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
    intl,
    DmsModal,
    DmsButton,
    fetchUrl,
    fetchData,
    HTTP_STATUS,
    LanguageBaseContext,
} from "dmsbasereact";
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Button,
    IconButton,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DmsAutocomplete } from "../../components/DmsAutoComplete/DmsAutoComplete";
import { DocumentUploadSlot } from "../../components/DocumentUploadSlot/DocumentUploadSlot";
import { DmsRichTextEditor } from "../../components/DmsRichTextEditor";

export const CreateNoticeModal = ({ open, onClose, onSaved }) => {
    const { language } = useContext(LanguageBaseContext);

    const [noticeType, setNoticeType] = useState("");
    const [institutionPk, setInstitutionPk] = useState(null);
    const [title, setTitle] = useState("");
    const [text, setText] = useState("");
    const [fileSlots, setFileSlots] = useState([{ id: 0, file: null }]);
    const [slotCounter, setSlotCounter] = useState(1);
    const [saving, setSaving] = useState(false);
    const [typeOptions, setTypeOptions] = useState([]);
    const [editorKey, setEditorKey] = useState(0);

    useEffect(() => {
        const fetchTypes = async () => {
            try {
                const response = await fetchData("/system_notice/types", "GET", true);
                const data = response?.data?.data || response?.data || [];
                setTypeOptions(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Failed to fetch notice types:", error);
            }
        };
        fetchTypes();
    }, [language]);

    useEffect(() => {
        if (open) {
            setNoticeType("");
            setInstitutionPk(null);
            setTitle("");
            setText("");
            setFileSlots([{ id: 0, file: null }]);
            setSlotCounter(1);
            setEditorKey((k) => k + 1);
        }
    }, [open]);

    const handleAddFileSlot = useCallback(() => {
        setFileSlots((prev) => [...prev, { id: slotCounter, file: null }]);
        setSlotCounter((c) => c + 1);
    }, [slotCounter]);

    const handleRemoveFileSlot = useCallback((slotId) => {
        setFileSlots((prev) => prev.filter((s) => s.id !== slotId));
    }, []);

    const handleFileSelect = useCallback((slotId, file) => {
        setFileSlots((prev) =>
            prev.map((s) => (s.id === slotId ? { ...s, file } : s))
        );
    }, []);

    const uploadFiles = useCallback(async () => {
        const filePks = [];
        const filesToUpload = fileSlots.filter((s) => s.file);

        for (const slot of filesToUpload) {
            const res = await fetchUrl(
                "/notice_annotation/file_upload",
                "POST",
                true,
                slot.file,
                false,
                true,
                null,
                null,
                "multipart/form-data"
            );
            if (
                res.status === HTTP_STATUS.CREATED_201 ||
                res.status === HTTP_STATUS.OK_200
            ) {
                const pk = res.data?.pk || res.data?.data?.pk;
                if (pk) filePks.push(pk);
            }
        }
        return filePks;
    }, [fileSlots]);

    const isTextEmpty = !text || text === "<p><br></p>" || text.replace(/<[^>]*>/g, "").trim() === "";
    const canSave = noticeType !== "" && title.trim() && !isTextEmpty && !saving;

    const handleSave = useCallback(async () => {
        if (!canSave) return;

        setSaving(true);
        try {
            const filePks = await uploadFiles();

            await fetchData("/system_notice", "POST", true, {
                notice_type: Number(noticeType),
                institution_pk: institutionPk || null,
                annotation_title: title,
                annotation_text: text,
                file_pks: filePks,
            });

            onSaved();
        } catch (error) {
            console.error("Failed to create notice:", error);
        } finally {
            setSaving(false);
        }
    }, [canSave, noticeType, institutionPk, title, text, uploadFiles, onSaved]);

    return (
        <DmsModal isVisible={open} onClose={onClose} maxWidth="md" fullWidth>
            <DmsModal.Header
                title={intl.get("CreateNotice").d("Create Notice")}
                onClose={onClose}
            />
            <DmsModal.Body>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
                    <FormControl fullWidth required disabled={saving}>
                        <InputLabel>{intl.get("NoticeType").d("Type")}</InputLabel>
                        <Select
                            value={noticeType}
                            label={intl.get("NoticeType").d("Type")}
                            onChange={(e) => setNoticeType(e.target.value)}
                        >
                            {typeOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.translated_label || opt.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <DmsAutocomplete
                        label={intl.get("NoticeInstitution").d("Institution")}
                        value={institutionPk}
                        onChange={(val) => setInstitutionPk(val || null)}
                        url="/institution/all/approved"
                        properties={{ label: "name", value: "organization_branch_pk" }}
                        needs_authentication={true}
                        disabled={saving}
                        minInputLength={3}
                    />

                    <TextField
                        label={intl.get("AnnotationTitle").d("Title")}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        required
                        disabled={saving}
                    />

                    <DmsRichTextEditor
                        key={editorKey}
                        label={intl.get("AnnotationText").d("Text")}
                        value={text}
                        onChange={setText}
                        placeholder={intl.get("AnnotationTextPlaceholder").d("Write the annotation text...")}
                        disabled={saving}
                    />

                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                            <Typography variant="subtitle2">
                                {intl.get("AttachedFiles").d("Attached Files")}
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddFileSlot}
                                disabled={saving}
                            >
                                {intl.get("AddFile").d("Add File")}
                            </Button>
                        </Box>

                        {fileSlots.map((slot) => (
                            <Box
                                key={slot.id}
                                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <DocumentUploadSlot
                                        docName={intl.get("SelectFile").d("Select File")}
                                        file={slot.file}
                                        onFileSelect={(_, file) => handleFileSelect(slot.id, file)}
                                        disabled={saving}
                                    />
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveFileSlot(slot.id)}
                                    disabled={saving}
                                    color="error"
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Box>
                        ))}

                        {fileSlots.length === 0 && (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 1 }}>
                                {intl.get("NoFilesAttached").d("No files attached")}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <DmsButton
                    onClick={onClose}
                    text={intl.get("Cancel").d("Cancel")}
                    disabled={saving}
                />
                <DmsButton
                    onClick={handleSave}
                    variant="primary"
                    text={intl.get("Save").d("Save")}
                    disabled={!canSave}
                />
            </DmsModal.Footer>
        </DmsModal>
    );
};

CreateNoticeModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
};
